import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Trash2, Plus, RefreshCw, Sprout, FileUp, TrendingUp, AlertTriangle } from 'lucide-react';
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const MONTHS_FI = ['Tam', 'Hel', 'Maa', 'Huh', 'Tou', 'Kes', 'Hei', 'Elo', 'Syy', 'Lok', 'Mar', 'Jou'];
const YEAR_COLORS = ['#2A4B30', '#C05A3C', '#5A7DA0', '#B69340', '#7C5A9A', '#3F6B45', '#A05050'];

function num(n) {
  return Number(n || 0).toLocaleString('fi-FI', { maximumFractionDigits: 1 });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function Harvest() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const currentYear = new Date().getFullYear();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedYears, setSelectedYears] = useState(new Set());
  const [showAverage, setShowAverage] = useState(true);
  const [form, setForm] = useState({ date: todayIso(), kg: '', pickers: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dupCheck, setDupCheck] = useState(null); // {duplicate_dates, totals} tai null
  const [dupChecking, setDupChecking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, sumRes] = await Promise.all([
        api.get('/harvest'),
        api.get('/harvest/summary'),
      ]);
      setEntries(listRes.data.entries || []);
      setSummary(sumRes.data);
      // Auto-valitse uudet vuodet: alkuun kaikki, myöhemmin merge uudet
      setSelectedYears((prev) => {
        const ys = (sumRes.data?.years || []).map(String);
        if (prev.size === 0) return new Set(ys);
        const merged = new Set(prev);
        ys.forEach((y) => merged.add(y));
        return merged;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lataus epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const checkDuplicates = async () => {
    if (!isAdmin) return;
    setDupChecking(true);
    try {
      const r = await api.get('/harvest/admin/duplicates');
      setDupCheck(r.data);
      const t = r.data.totals;
      if (t.dates_with_duplicates === 0) {
        toast.success('Ei duplikaatteja! Kaikki päivät ovat uniikkeja.');
      } else {
        toast.warning(
          `${t.dates_with_duplicates} päivällä on duplikaatteja (${t.excess_entries_to_remove} ylimääräistä riviä, ${t.excess_kg_removed.toLocaleString('fi-FI')} kg poistuisi siivouksella).`,
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplikaattitarkistus epäonnistui');
    } finally {
      setDupChecking(false);
    }
  };

  const cleanupDuplicates = async () => {
    if (!isAdmin) return;
    if (!dupCheck || dupCheck.totals.dates_with_duplicates === 0) return;
    const t = dupCheck.totals;
    if (!window.confirm(
      `Siivotaan duplikaatit:\n\n` +
      `• ${t.dates_with_duplicates} päivää joilla duplikaatteja\n` +
      `• ${t.excess_entries_to_remove} riviä poistetaan\n` +
      `• ${t.excess_kg_removed.toLocaleString('fi-FI')} kg vähenee kokonaissummasta\n\n` +
      `Uusin kirjaus per päivä säilyy. Jatketaanko?`
    )) return;
    try {
      const r = await api.post('/harvest/admin/cleanup-duplicates');
      const d = r.data;
      toast.success(`Poistettu ${d.removed_entries} riviä (${d.removed_kg.toLocaleString('fi-FI')} kg) ${d.affected_dates} päivältä.`);
      setDupCheck(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Siivous epäonnistui');
    }
  };

  const importHistorical = async () => {
    if (!isAdmin) return;
    const confirmMsg =
      'Tuo Excel-historiadata (2021–2026, 300 kirjausta) tietokantaan.\n\n' +
      'Toiminto on turvallinen ajaa uudelleen — jo olemassa olevat päivämäärät ohitetaan.\n\nJatketaanko?';
    if (!window.confirm(confirmMsg)) return;
    setImporting(true);
    try {
      const r = await api.post('/harvest/admin/import-historical');
      const d = r.data || {};
      const yrs = Object.entries(d.by_year || {})
        .map(([y, n]) => `${y}: ${n}`)
        .join(', ');
      if ((d.imported || 0) === 0) {
        toast.success(`Ei uusia tuotavia — kaikki ${d.skipped} kirjausta olivat jo tietokannassa.`);
      } else {
        toast.success(`Tuotu ${d.imported} kirjausta (${yrs}). Ohitettu ${d.skipped}.`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tuonti epäonnistui');
    } finally {
      setImporting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || form.kg === '') {
      toast.error('Päivä ja määrä ovat pakollisia');
      return;
    }
    setSaving(true);
    try {
      await api.post('/harvest', {
        date: form.date,
        kg: Number(form.kg),
        pickers: Number(form.pickers || 0),
        note: '',
      });
      setForm({ date: todayIso(), kg: '', pickers: '' });
      toast.success('Poimintakirjaus lisätty');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tallennus epäonnistui');
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id) => {
    if (!confirm('Poistetaanko poimintakirjaus?')) return;
    try {
      await api.delete(`/harvest/${id}`);
      toast.success('Poistettu');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Poisto epäonnistui');
    }
  };

  // Ruudukko-tila: state ja käytännöllinen kumul.tila-toggle
  const [gridCell, setGridCell] = useState(null); // {year, m, d, value}
  const [gridSaving, setGridSaving] = useState(false);
  const [gridMode, setGridMode] = useState('daily'); // 'daily' | 'cumulative'

  const years = summary?.years || [];
  const toggleYear = (y) => {
    const ys = String(y);
    const ns = new Set(selectedYears);
    if (ns.has(ys)) ns.delete(ys); else ns.add(ys);
    setSelectedYears(ns);
  };

  const entriesByDate = useMemo(() => {
    const m = new Map();
    for (const e of entries) m.set(e.date, e);
    return m;
  }, [entries]);

  const gridDays = useMemo(() => {
    // 15.6. → 15.9. = 93 päivää. Kattaa myöhäiskauden syyskuulle asti,
    // vaikka useimmat kaudet päättyvät elokuussa.
    const out = [];
    const start = { m: 6, d: 15 };
    const end = { m: 9, d: 15 };
    let m = start.m;
    let d = start.d;
    while (m < end.m || (m === end.m && d <= end.d)) {
      out.push({ m, d });
      const days_in_month = new Date(2020, m, 0).getDate();
      d += 1;
      if (d > days_in_month) { d = 1; m += 1; }
    }
    return out;
  }, []);

  const selectedYearsSorted = useMemo(
    () => years.filter((y) => selectedYears.has(String(y))).sort((a, b) => a - b),
    [years, selectedYears],
  );

  // Kumulatiiviset kg-summat per vuosi ja päivä (forward-fill: puuttuvien päivien
  // solu näyttää edellisen päivän summan, jotta kausin kertymä näkyy jatkuvana).
  // Muoto: cumMap[year][MM-DD] = kertynyt kg tähän päivään mennessä (0 = ei vielä alkanut).
  const cumMap = useMemo(() => {
    const out = {};
    for (const y of selectedYearsSorted) {
      out[y] = {};
      let running = 0;
      for (const { m, d } of gridDays) {
        const dateKey = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const entry = entriesByDate.get(dateKey);
        if (entry) running += entry.kg;
        out[y][`${m}-${d}`] = running;
      }
    }
    return out;
  }, [selectedYearsSorted, gridDays, entriesByDate]);

  const saveGridCell = async (year, m, d, rawValue) => {
    const kgNum = Number(String(rawValue).replace(',', '.').trim());
    if (rawValue === '' || Number.isNaN(kgNum) || kgNum < 0) {
      setGridCell(null);
      return;
    }
    const date = `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const existing = entriesByDate.get(date);
    setGridSaving(true);
    try {
      if (existing) {
        if (Math.abs(existing.kg - kgNum) < 0.001) { setGridCell(null); return; }
        await api.put(`/harvest/${existing.id}`, {
          date, kg: kgNum,
          pickers: existing.pickers || 0,
          note: existing.note || '',
        });
        toast.success(`Päivitetty ${d}.${m}.${year}`);
      } else {
        await api.post('/harvest', { date, kg: kgNum, pickers: 0, note: '' });
        toast.success(`Lisätty ${d}.${m}.${year}: ${num(kgNum)} kg`);
      }
      setGridCell(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tallennus epäonnistui');
    } finally {
      setGridSaving(false);
    }
  };

  // Rakenna kuvaajan data: x = päivän numero vuodessa (doy), y = kumulatiivinen kg
  // Lisäksi lasketaan historiavuosien (kaikki paitsi nykyinen) keskiarvo per doy.
  const { chartData, avgTotalKg, avgYearsCount } = useMemo(() => {
    if (!summary) return { chartData: [], avgTotalKg: 0, avgYearsCount: 0 };
    const map = new Map(); // doy -> {doy, 2024_kg, 2025_kg, ...}
    for (const y of years) {
      if (!selectedYears.has(String(y))) continue;
      const pts = summary.by_year_day_of_year?.[String(y)] || [];
      for (const p of pts) {
        const existing = map.get(p.doy) || { doy: p.doy };
        existing[`${y}_kg`] = p.kg_cum;
        existing[`${y}_pickers`] = p.pickers_cum;
        map.set(p.doy, existing);
      }
    }

    // Historic years for average: all years present in summary except current year
    const historicYears = years.filter((y) => Number(y) !== currentYear);
    // Build doy → kg_cum per historic year (each year's last known cum forward-filled)
    const historyByYear = {};
    for (const y of historicYears) {
      const pts = summary.by_year_day_of_year?.[String(y)] || [];
      const perDoy = new Map();
      let lastCum = 0;
      const sorted = [...pts].sort((a, b) => a.doy - b.doy);
      for (const p of sorted) {
        lastCum = p.kg_cum;
        perDoy.set(p.doy, lastCum);
      }
      historyByYear[y] = { perDoy, maxDoy: sorted.length ? sorted[sorted.length - 1].doy : 0 };
    }

    // Collect all doys where at least one year has data (or the average line has points)
    const allDoys = new Set([...map.keys()]);
    for (const y of historicYears) {
      for (const d of historyByYear[y].perDoy.keys()) allDoys.add(d);
    }

    // For each doy compute average of forward-filled cum values across historic years
    // (only include years that have reached that doy in their season)
    for (const doy of allDoys) {
      let sum = 0;
      let cnt = 0;
      for (const y of historicYears) {
        const hy = historyByYear[y];
        if (!hy || doy > hy.maxDoy) continue; // year hasn't reached this doy → exclude
        // Forward-fill: last known cum <= doy
        let val = null;
        for (let d = doy; d >= 1; d--) {
          if (hy.perDoy.has(d)) { val = hy.perDoy.get(d); break; }
        }
        if (val !== null) { sum += val; cnt += 1; }
      }
      if (cnt > 0) {
        const existing = map.get(doy) || { doy };
        existing.avg_kg = Math.round(sum / cnt);
        map.set(doy, existing);
      }
    }

    // Compute end-of-season average total (avg of each historic year's final cum)
    let totalSum = 0;
    let totalCnt = 0;
    for (const y of historicYears) {
      const hy = historyByYear[y];
      if (!hy || hy.maxDoy === 0) continue;
      const finalCum = hy.perDoy.get(hy.maxDoy) || 0;
      totalSum += finalCum;
      totalCnt += 1;
    }

    return {
      chartData: [...map.values()].sort((a, b) => a.doy - b.doy),
      avgTotalKg: totalCnt > 0 ? Math.round(totalSum / totalCnt) : 0,
      avgYearsCount: totalCnt,
    };
  }, [summary, selectedYears, years, currentYear]);

  const kgPerPicker = (y) => {
    const t = summary?.by_year_totals?.[String(y)];
    if (!t || !t.pickers) return 0;
    return t.kg / t.pickers;
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="harvest-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[#2A4B30]">
            <Sprout size={20} />
            <h1 className="text-xl font-medium">Poimintamäärät</h1>
          </div>
          <p className="text-sm text-[#6A6D66] mt-1">
            Kirjaa päivittäiset poimintamäärät ja poimijamäärä. Vertaile vuosittaisia eroja.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <Button
              variant="outline"
              onClick={checkDuplicates}
              disabled={dupChecking}
              data-testid="harvest-check-duplicates"
              title="Etsi päivämäärät joilla on useita kirjauksia (voi nostaa summaa väärin)"
              className="h-9 rounded-full border-[#C05A3C] text-[#C05A3C] hover:bg-[#FBEAE5]"
            >
              <AlertTriangle size={14} className={`mr-1.5 ${dupChecking ? 'animate-pulse' : ''}`} />
              {dupChecking ? 'Tarkistetaan…' : 'Etsi duplikaatit'}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={importHistorical}
              disabled={importing}
              data-testid="harvest-import-historical"
              title="Tuo Excel-historiadata (2021–2026) kertaalleen tietokantaan"
              className="h-9 rounded-full border-[#2A4B30] text-[#2A4B30] hover:bg-[#E8F0E9]"
            >
              <FileUp size={14} className={`mr-1.5 ${importing ? 'animate-pulse' : ''}`} />
              {importing ? 'Tuodaan…' : 'Tuo historia'}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={load}
            data-testid="harvest-reload"
            className="h-9 rounded-full"
          >
            <RefreshCw size={14} className="mr-1.5" /> Päivitä
          </Button>
        </div>
      </div>

      {/* Duplikaattiraportti (näkyy tarkistuksen jälkeen) */}
      {dupCheck && dupCheck.totals.dates_with_duplicates > 0 && (
        <div className="border border-[#F0CFC5] bg-[#FBEAE5] rounded-xl p-4 space-y-3" data-testid="harvest-dup-report">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-[#C05A3C] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-[#7A3520]">
                Löytyi {dupCheck.totals.dates_with_duplicates} päivää joilla on useita kirjauksia
              </div>
              <div className="text-sm text-[#7A3520] mt-1">
                Yhteensä <strong>{dupCheck.totals.excess_entries_to_remove}</strong> ylimääräistä riviä ·
                Kokonaissumma vähenisi <strong>{dupCheck.totals.excess_kg_removed.toLocaleString('fi-FI')} kg</strong> siivouksella.
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDupCheck(null)}
                data-testid="harvest-dup-close"
                className="h-8 rounded-full"
              >
                Sulje
              </Button>
              <Button
                size="sm"
                onClick={cleanupDuplicates}
                data-testid="harvest-dup-cleanup"
                className="h-8 rounded-full bg-[#C05A3C] hover:bg-[#9F4831] text-white"
              >
                Siivoa (uusin säilyy)
              </Button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto border border-[#F0CFC5] rounded bg-white">
            <table className="w-full text-xs">
              <thead className="bg-[#F4F5F0] sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Päivä</th>
                  <th className="px-3 py-2 text-right">Kirjauksia</th>
                  <th className="px-3 py-2 text-right">Säilyy (kg)</th>
                  <th className="px-3 py-2 text-right">Poistuu (kg)</th>
                  <th className="px-3 py-2 text-left">Erittelyt</th>
                </tr>
              </thead>
              <tbody>
                {dupCheck.duplicate_dates.map((d) => (
                  <tr key={d.date} className="border-t border-[#F0CFC5]">
                    <td className="px-3 py-2 font-mono">{d.date}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{d.count}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#2A4B30] font-medium">
                      {d.kept_kg.toLocaleString('fi-FI')}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#C05A3C]">
                      {d.extra_kg_total.toLocaleString('fi-FI')}
                    </td>
                    <td className="px-3 py-2 text-[10px] text-[#6A6D66]">
                      {d.entries.map((e, i) => (
                        <div key={e.id} className={i === 0 ? 'font-medium text-[#2A4B30]' : ''}>
                          {i === 0 ? '→ ' : '  '}
                          {e.kg.toLocaleString('fi-FI')} kg · {e.created_by} · {(e.updated_at || e.created_at || '').slice(0, 16).replace('T', ' ')}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lomake */}
      <form
        onSubmit={submit}
        className="grid grid-cols-1 md:grid-cols-[160px_140px_140px_auto] gap-3 items-end bg-[#F4F5F0] p-4 rounded-lg"
        data-testid="harvest-form"
      >
        <div>
          <label className="text-xs text-[#6A6D66] block mb-1">Päivä</label>
          <Input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="harvest-form-date"
            required
          />
        </div>
        <div>
          <label className="text-xs text-[#6A6D66] block mb-1">Määrä (kg)</label>
          <Input
            type="number"
            step="0.1"
            min="0"
            value={form.kg}
            onChange={(e) => setForm({ ...form, kg: e.target.value })}
            data-testid="harvest-form-kg"
            required
          />
        </div>
        <div>
          <label className="text-xs text-[#6A6D66] block mb-1">Poimijoita</label>
          <Input
            type="number"
            step="1"
            min="0"
            value={form.pickers}
            onChange={(e) => setForm({ ...form, pickers: e.target.value })}
            data-testid="harvest-form-pickers"
          />
        </div>
        <Button
          type="submit"
          disabled={saving}
          data-testid="harvest-form-submit"
          className="h-10 bg-[#2A4B30] hover:bg-[#1F3A24] text-white"
        >
          <Plus size={14} className="mr-1" /> Lisää
        </Button>
      </form>

      {/* Vuosisuodatus */}
      {years.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="harvest-year-filter">
          <span className="text-xs text-[#6A6D66] mr-2">Näytä vuodet:</span>
          {years.map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => toggleYear(y)}
              data-testid={`harvest-year-toggle-${y}`}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${
                selectedYears.has(String(y))
                  ? 'bg-[#2A4B30] text-white'
                  : 'bg-[#F4F5F0] text-[#6A6D66] hover:bg-[#E5E6E3]'
              }`}
            >
              {y}
            </button>
          ))}
        </div>
      )}

      {/* Vuositaulukko */}
      {summary && years.length > 0 && (
        <div className="overflow-x-auto border border-[#E4E7DF] rounded" data-testid="harvest-year-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F5F0] text-[#3A4038]">
              <tr>
                <Th>Vuosi</Th>
                {MONTHS_FI.map((m) => <Th key={m} className="text-right">{m}</Th>)}
                <Th className="text-right">Yhteensä (kg)</Th>
                <Th className="text-right">Poimijat</Th>
                <Th className="text-right">kg/poimija</Th>
                <Th className="text-right">Päiviä</Th>
              </tr>
            </thead>
            <tbody>
              {years.filter((y) => selectedYears.has(String(y))).map((y) => {
                const months = summary.by_year_month?.[String(y)] || {};
                const totals = summary.by_year_totals?.[String(y)] || { kg: 0, pickers: 0, days: 0 };
                return (
                  <tr key={y} className="border-t border-[#E4E7DF]">
                    <Td className="font-medium">{y}</Td>
                    {MONTHS_FI.map((_, i) => {
                      const m = months[String(i + 1)];
                      return (
                        <Td key={i} className="text-right tabular-nums">
                          {m ? num(m.kg) : '—'}
                        </Td>
                      );
                    })}
                    <Td className="text-right tabular-nums font-medium">{num(totals.kg)}</Td>
                    <Td className="text-right tabular-nums">{totals.pickers}</Td>
                    <Td className="text-right tabular-nums">{num(kgPerPicker(y))}</Td>
                    <Td className="text-right tabular-nums">{totals.days}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Kuvaaja: kumulatiivinen kg per vuosi */}
      {chartData.length > 0 && (
        <div className="border border-[#E4E7DF] rounded p-4" data-testid="harvest-chart">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="text-sm text-[#3A4038]">Kumulatiivinen poimintamäärä (kg) päivittäin</div>
            {avgYearsCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAverage((v) => !v)}
                data-testid="harvest-toggle-average"
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs transition-colors ${
                  showAverage
                    ? 'bg-[#6A6D66] text-white hover:bg-[#4A4D46]'
                    : 'bg-[#F4F5F0] text-[#6A6D66] hover:bg-[#E5E6E3]'
                }`}
                title={`Keskiarvo lasketaan ${avgYearsCount} historiavuoden datasta (kaikki paitsi ${currentYear}). Kauden lopun keskiarvo: ${num(avgTotalKg)} kg.`}
              >
                <TrendingUp size={13} />
                Keskiarvo ({avgYearsCount}v)
              </button>
            )}
          </div>
          <div style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E7DF" />
                <XAxis
                  dataKey="doy"
                  tickFormatter={(doy) => {
                    // Muunna päiväluku päivämääräksi ilman vuosikontekstia (kk.pv)
                    const d = new Date(2024, 0, doy);
                    return `${d.getDate()}.${d.getMonth() + 1}.`;
                  }}
                  stroke="#6A6D66"
                  fontSize={11}
                />
                <YAxis stroke="#6A6D66" fontSize={11} />
                <Tooltip
                  formatter={(v) => `${num(v)} kg`}
                  labelFormatter={(doy) => {
                    const d = new Date(2024, 0, doy);
                    return `Päivä ${d.getDate()}.${d.getMonth() + 1}.`;
                  }}
                />
                <Legend />
                {years.filter((y) => selectedYears.has(String(y))).map((y, idx) => (
                  <Line
                    key={y}
                    type="monotone"
                    dataKey={`${y}_kg`}
                    name={String(y)}
                    stroke={YEAR_COLORS[idx % YEAR_COLORS.length]}
                    strokeWidth={Number(y) === currentYear ? 3 : 2}
                    dot={false}
                    connectNulls
                  />
                ))}
                {showAverage && avgYearsCount > 0 && (
                  <Line
                    type="monotone"
                    dataKey="avg_kg"
                    name={`Keskiarvo (${avgYearsCount}v)`}
                    stroke="#6A6D66"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={false}
                    connectNulls
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {avgYearsCount > 0 && (() => {
            const cyPts = summary?.by_year_day_of_year?.[String(currentYear)] || [];
            const cyKg = cyPts.length ? cyPts[cyPts.length - 1].kg_cum : 0;
            const pct = avgTotalKg > 0 ? Math.round((cyKg / avgTotalKg) * 100) : 0;
            return (
              <div
                className="mt-3 text-xs text-[#6A6D66] flex flex-wrap items-center gap-x-4 gap-y-1"
                data-testid="harvest-forecast-summary"
              >
                <span>
                  Kauden lopun keskiarvo <strong className="text-[#3A4038]">{num(avgTotalKg)} kg</strong>{' '}
                  ({avgYearsCount} historiavuoden pohjalta)
                </span>
                {cyKg > 0 && (
                  <span>
                    {currentYear} tähän mennessä <strong className="text-[#2A4B30]">{num(cyKg)} kg</strong>{' '}
                    ({pct}% keskiarvosta)
                  </span>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Päivittäinen ruudukko: rivit=päivät, sarakkeet=vuodet — puuttuvat solut helppo täydentää */}
      {selectedYearsSorted.length > 0 && (
        <div className="border border-[#E4E7DF] rounded" data-testid="harvest-grid">
          <div className="flex items-center justify-between flex-wrap gap-2 px-3 py-2 border-b border-[#E4E7DF] bg-[#F4F5F0]">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-sm text-[#3A4038] font-medium">Päivittäinen ruudukko (15.6.–15.9.)</div>
              <div className="inline-flex items-center rounded-full bg-white border border-[#E4E7DF] p-0.5" data-testid="harvest-grid-mode">
                <button
                  type="button"
                  onClick={() => setGridMode('daily')}
                  data-testid="harvest-grid-mode-daily"
                  className={`h-7 px-3 rounded-full text-xs transition-colors ${
                    gridMode === 'daily'
                      ? 'bg-[#2A4B30] text-white'
                      : 'text-[#6A6D66] hover:text-[#1C1C1A]'
                  }`}
                >
                  Päivittäin
                </button>
                <button
                  type="button"
                  onClick={() => setGridMode('cumulative')}
                  data-testid="harvest-grid-mode-cumulative"
                  className={`h-7 px-3 rounded-full text-xs transition-colors ${
                    gridMode === 'cumulative'
                      ? 'bg-[#2A4B30] text-white'
                      : 'text-[#6A6D66] hover:text-[#1C1C1A]'
                  }`}
                >
                  Kumulatiivinen
                </button>
              </div>
            </div>
            <div className="text-xs text-[#6A6D66]">
              Klikkaa solua täyttääksesi tai muokataksesi. <span className="inline-block w-3 h-3 rounded bg-[#FBEAE5] border border-[#F0CFC5] align-middle mx-1"></span>puuttuu ·
              <span className="inline-block w-3 h-3 rounded bg-white border border-[#E4E7DF] align-middle mx-1"></span>on kirjaus
            </div>
          </div>
          <div className="overflow-auto max-h-[500px]">
            <table className="w-full text-xs" data-testid="harvest-grid-table">
              <thead className="bg-white sticky top-0 z-10">
                <tr>
                  <th className="sticky left-0 z-20 bg-white px-2 py-2 text-left border-b border-r border-[#E4E7DF] w-16 text-[11px] font-medium text-[#3A4038]">Päivä</th>
                  {selectedYearsSorted.map((y) => (
                    <th key={y} className="px-2 py-2 border-b border-[#E4E7DF] text-right text-[11px] font-medium text-[#3A4038] min-w-[80px]">
                      {y}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gridDays.map(({ m, d }, idx) => {
                  const isMonthStart = d === 1 || idx === 0;
                  return (
                    <tr key={`${m}-${d}`} className={isMonthStart ? 'border-t-2 border-[#CFD3C5]' : 'border-t border-[#E4E7DF]'}>
                      <td className="sticky left-0 z-10 bg-white px-2 py-1.5 border-r border-[#E4E7DF] text-[11px] tabular-nums text-[#6A6D66] font-mono">
                        {d}.{m}.
                      </td>
                      {selectedYearsSorted.map((y) => {
                        const date = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                        const entry = entriesByDate.get(date);
                        const isEditing = gridCell && gridCell.year === y && gridCell.m === m && gridCell.d === d;
                        const isFuture = new Date(date) > new Date();
                        const cellBg = entry ? 'bg-white hover:bg-[#F4F5F0]'
                          : isFuture ? 'bg-[#F4F5F0]'
                          : 'bg-[#FBEAE5] hover:bg-[#F5D6CD]';
                        const cumKg = cumMap[y] ? cumMap[y][`${m}-${d}`] || 0 : 0;
                        const seasonStarted = cumKg > 0;
                        return (
                          <td
                            key={y}
                            className={`px-1 py-0.5 text-right tabular-nums border-r border-[#E4E7DF] ${cellBg} ${isFuture ? 'cursor-default' : 'cursor-pointer'}`}
                            onClick={() => {
                              if (isFuture || isEditing) return;
                              setGridCell({ year: y, m, d, value: entry ? String(entry.kg) : '' });
                            }}
                            data-testid={`harvest-cell-${date}`}
                            title={entry
                              ? `${num(entry.kg)} kg · kertymä ${num(cumKg)} kg`
                              : seasonStarted
                                ? `Ei kirjausta · kertymä ${num(cumKg)} kg`
                                : 'Ei kirjausta'}
                          >
                            {isEditing ? (
                              <input
                                type="text"
                                inputMode="decimal"
                                autoFocus
                                value={gridCell.value}
                                onChange={(ev) => setGridCell({ ...gridCell, value: ev.target.value })}
                                onKeyDown={(ev) => {
                                  if (ev.key === 'Enter') {
                                    ev.preventDefault();
                                    saveGridCell(y, m, d, gridCell.value);
                                  } else if (ev.key === 'Escape') {
                                    setGridCell(null);
                                  }
                                }}
                                onBlur={() => saveGridCell(y, m, d, gridCell.value)}
                                disabled={gridSaving}
                                className="w-full h-7 px-1 border border-[#2A4B30] rounded text-right text-xs bg-white outline-none focus:ring-2 focus:ring-[#2A4B30]/40"
                                data-testid={`harvest-cell-input-${date}`}
                              />
                            ) : gridMode === 'cumulative' ? (
                              // Kumulatiivinen tila: näytä kertymä. Puuttuvat päivät himmennettynä.
                              entry ? (
                                <span className="text-[#1C1C1A]">{num(cumKg)}</span>
                              ) : isFuture ? (
                                <span className="text-[#B4B7AF]">·</span>
                              ) : seasonStarted ? (
                                <span className="text-[#B4B7AF] italic">{num(cumKg)}</span>
                              ) : (
                                <span className="text-[#C05A3C] opacity-70">+</span>
                              )
                            ) : (
                              // Päivittäin-tila: näytä vain sen päivän arvo
                              entry ? (
                                <span className="text-[#1C1C1A]">{num(entry.kg)}</span>
                              ) : isFuture ? (
                                <span className="text-[#B4B7AF]">·</span>
                              ) : (
                                <span className="text-[#C05A3C] opacity-70">+</span>
                              )
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Kirjausten lista */}
      <div className="border border-[#E4E7DF] rounded" data-testid="harvest-entries-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F5F0] text-[#3A4038]">
            <tr>
              <Th>Päivä</Th>
              <Th className="text-right">Määrä (kg)</Th>
              <Th className="text-right">Poimijoita</Th>
              <Th className="text-right">kg/poimija</Th>
              <Th>Kirjaaja</Th>
              <Th className="text-right">Toiminnot</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><Td colSpan={6} className="text-center text-[#6A6D66] py-6">Ladataan…</Td></tr>
            ) : entries.length === 0 ? (
              <tr><Td colSpan={6} className="text-center text-[#6A6D66] py-6">Ei kirjauksia vielä</Td></tr>
            ) : entries.map((e) => (
              <tr key={e.id} className="border-t border-[#E4E7DF]" data-testid={`harvest-row-${e.id}`}>
                <Td>{new Date(e.date).toLocaleDateString('fi-FI')}</Td>
                <Td className="text-right tabular-nums font-medium">{num(e.kg)}</Td>
                <Td className="text-right tabular-nums">{e.pickers || '—'}</Td>
                <Td className="text-right tabular-nums">
                  {e.pickers ? num(e.kg / e.pickers) : '—'}
                </Td>
                <Td className="text-xs text-[#6A6D66]">{e.created_by || '—'}</Td>
                <Td className="text-right">
                  <button
                    type="button"
                    onClick={() => removeEntry(e.id)}
                    data-testid={`harvest-delete-${e.id}`}
                    className="w-8 h-8 rounded-full hover:bg-[#FBEAE5] text-[#6A6D66] hover:text-[#C05A3C] flex items-center justify-center transition-colors ml-auto"
                    title="Poista kirjaus"
                  >
                    <Trash2 size={14} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = '' }) {
  return <th className={`text-left px-3 py-2 text-[11px] font-medium ${className}`}>{children}</th>;
}

function Td({ children, className = '', ...rest }) {
  return <td className={`px-3 py-2 text-[13px] align-top ${className}`} {...rest}>{children}</td>;
}
